import type {
  AnalysisHistoryResponse,
  AnalysisListItem,
  AnalysisSnapshot,
  CreateAnalysisResponse,
  Fixture,
  FixtureSearchResponse,
  ProviderResult,
  Quote,
  ValueOddsFilters,
} from "@tracker-bet-analystic/domain";
import { supabase, supabaseEnv } from "./supabase";

/**
 * T05 — cliente do serviço central (Edge Function `analysis`).
 * O browser NUNCA fala com o provedor de futebol: apenas com esta função, autenticada
 * com o JWT da sessão. O workspace/user são derivados no backend.
 */

export type AnalysisAction = "search" | "create" | "get" | "history" | "coverage" | "match_detail" | "consensus_odds" | "value_odds_feed" | "chat" | "chat_history" | "chat_conversations" | "chat_conversation_create" | "football_data_probe";

export interface ValueOddsFeedPayload {
  items: Array<{ analysisId: string; snapshot: AnalysisSnapshot }>;
  quotes: Quote[];
  fixtureCount: number;
  previewCount: number;
  generatedAt: string;
  cacheHit: boolean;
  oddsCoverage?: {
    fixturesChecked: number;
    consensusFixtures: number;
    bookmakerFixtures: number;
  };
}

export interface AgentChatMessage {
  id: string;
  analysisId?: string;
  conversationId?: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
}

export interface ApiFailure {
  ok: false;
  error: string;
  message?: string;
}

export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

const FRIENDLY_ERRORS: Record<string, string> = {
  missing_token: "Sessão expirada. Entre novamente.",
  invalid_token: "Sessão inválida. Entre novamente.",
  workspace_not_found: "Não encontramos um espaço para este usuário.",
  missing_fixture: "Partida não informada.",
  missing_analysis: "Análise não informada.",
  unknown_action: "Operação não suportada.",
  fixture_unknown: "Partida não encontrada na base canônica.",
  fixture_cancelled: "Partida cancelada não é analisada.",
  fixture_postponed: "Partida adiada não é analisada.",
  fixture_unresolved: "Partida sem condição suficiente para análise.",
  fixture_abandoned: "Partida abandonada não é analisada.",
  fixture_awarded: "Resultado atribuído por decisão não é analisado.",
  not_found: "Análise não encontrada neste espaço.",
  invalid_cutoff: "Horário de corte inválido para esta partida.",
  internal_error: "Falha ao falar com o serviço de análise.",
  not_configured: "O provedor de futebol não está configurado no servidor.",
  missing_message: "Escreva uma pergunta antes de enviar.",
  invalid_message: "A pergunta precisa ter entre 1 e 2.000 caracteres.",
  invalid_message_id: "Não foi possível identificar esta mensagem. Tente novamente.",
};

export function describeApiError(failure: ApiFailure): string {
  if (failure.message && failure.error === "internal_error") {
    if (failure.message.includes("não configurado")) {
      return "O provedor de futebol não está configurado no servidor (credencial ausente). A busca só funciona com dados já em cache.";
    }
    return failure.message;
  }
  return FRIENDLY_ERRORS[failure.error] ?? "Não foi possível concluir a operação.";
}

async function invoke<T>(
  action: AnalysisAction,
  body: Record<string, unknown>,
  options: { signal?: AbortSignal } = {}
): Promise<ApiResult<T>> {
  if (!supabase) return { ok: false, error: "not_configured", message: "Supabase não configurado" };

  const { data, error } = await supabase.functions.invoke("analysis", {
    body: { action, ...body },
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (error) {
    // Erros HTTP do FunctionsError trazem o corpo em error.context
    let parsed: { error?: string; message?: string } | null = null;
    const context = (error as { context?: Response }).context;
    if (context && typeof context.json === "function") {
      try {
        parsed = (await context.json()) as { error?: string; message?: string };
      } catch {
        parsed = null;
      }
    }
    return { ok: false, error: parsed?.error ?? "internal_error", message: parsed?.message ?? error.message };
  }

  if (!data || typeof data !== "object") {
    return { ok: false, error: "internal_error", message: "resposta vazia" };
  }

  const payload = data as { ok?: boolean; data?: T; error?: string; message?: string };
  if (!payload.ok) {
    return { ok: false, error: payload.error ?? "internal_error", message: payload.message };
  }
  return { ok: true, data: (payload.data ?? null) as T };
}

export function searchFixtures(
  body: {
    fromUtc: string;
    toUtc: string;
    query?: string;
    competitionId?: string;
  },
  options: { signal?: AbortSignal } = {}
): Promise<ApiResult<FixtureSearchResponse>> {
  return invoke<FixtureSearchResponse>("search", body, options);
}

export function createAnalysis(body: {
  fixtureId: string;
  cutoffAt?: string;
}): Promise<ApiResult<CreateAnalysisResponse>> {
  return invoke<CreateAnalysisResponse>("create", body);
}

export function fetchAnalysis(analysisId: string): Promise<ApiResult<{ snapshot: AnalysisSnapshot }>> {
  return invoke<{ snapshot: AnalysisSnapshot }>("get", { analysisId });
}

export function fetchHistory(limit = 50): Promise<ApiResult<AnalysisHistoryResponse>> {
  return invoke<AnalysisHistoryResponse>("history", { limit });
}

/** P01 — diagnóstico de cobertura. */
export interface CoverageMetricPayload {
  key: string;
  label: string;
  games: number;
  homeValid: number;
  awayValid: number;
  minValid: number;
  coverage: number | null;
  level: "ALTA" | "MEDIA" | "BAIXA" | "INDISPONIVEL";
}

export interface CoverageRowPayload {
  competition_name: string;
  country: string | null;
  competition_external_id: string | null;
  sample_target: number;
  sample_size: number;
  period_from: string | null;
  period_to: string | null;
  metrics: Record<string, CoverageMetricPayload>;
  markets: { withData?: string[]; limited?: string[] };
  provider_calls: number;
  cache_hits: number;
  cache_misses: number;
  new_games: number;
  calculated_at: string;
}

export interface CoverageUpdatePayload {
  status: "updated" | "reused" | "busy";
  reused: boolean;
  busy: boolean;
  calculatedAt: string;
  competitions: Array<{ label: string; coverage: { games: number } }>;
  unavailable: string[];
  totals: { providerCalls: number; cacheHits: number; cacheMisses: number; newGames: number; analyzedGames: number };
}

export function fetchCoverage(): Promise<ApiResult<{ rows: CoverageRowPayload[]; role: string }>> {
  return invoke<{ rows: CoverageRowPayload[]; role: string }>("coverage", { mode: "status" });
}

export function updateCoverage(force = false): Promise<ApiResult<CoverageUpdatePayload>> {
  return invoke<CoverageUpdatePayload>("coverage", { mode: "update", force });
}

export interface FootballDataProbePayload {
  configured: boolean;
  secretName?: string;
  coverage?: { httpStatus: number; success: boolean; plan?: string | null; requestsUsed?: number | null; requestsLimit?: number | null; fields: string[] };
  fixtures?: { httpStatus: number; success: boolean; errorCode?: string | null; count: number };
  statistics?: { httpStatus: number; success: boolean; errorCode?: string | null; fields: string[] } | null;
  odds?: { httpStatus: number; success: boolean; errorCode?: string | null; fields: string[] } | null;
}

/** Teste restrito ao proprietário; o backend retorna somente status e campos, nunca a chave ou payload bruto. */
export function probeFootballData(): Promise<ApiResult<FootballDataProbePayload>> {
  return invoke<FootballDataProbePayload>("football_data_probe", {});
}

export type MatchDetailSection = "stats" | "players" | "lineup";

/** Detalhes são buscados pelo serviço central e cache-first; o navegador nunca chama o provider. */
export function fetchMatchDetail<T = unknown>(
  fixtureId: string,
  section: MatchDetailSection
): Promise<ApiResult<T>> {
  return invoke<T>("match_detail", { fixtureId, section });
}

/** Odds de consenso pré-jogo; não identifica uma casa nem altera snapshots salvos. */
export function fetchConsensusOdds(fixtureId: string): Promise<ApiResult<ProviderResult<Quote[]>>> {
  return invoke<ProviderResult<Quote[]>>("consensus_odds", { fixtureId });
}

/** Loads pre-game previews and current or last-known provider prices from the shared cache. */
export function fetchValueOddsFeed(fromUtc: string, toUtc: string): Promise<ApiResult<ValueOddsFeedPayload>> {
  return invoke<ValueOddsFeedPayload>("value_odds_feed", { fromUtc, toUtc });
}

/** Conversa vinculada ao snapshot; leitura e escrita passam pelo serviço autenticado. */
export function fetchChatMessages(analysisId: string): Promise<ApiResult<{ messages: AgentChatMessage[] }>> {
  return invoke<{ messages: AgentChatMessage[] }>("chat_history", { analysisId });
}

export function sendChatMessage(input: { analysisId: string; text: string; clientMessageId: string }): Promise<ApiResult<{ messageId: string; answer: string }>> {
  return invoke<{ messageId: string; answer: string }>("chat", input);
}

export interface AgentConversation {
  id: string;
  title: string;
  contextAnalysisId: string | null;
  createdAt: string;
  updatedAt: string;
}

export function fetchAgentConversations(): Promise<ApiResult<{ conversations: AgentConversation[] }>> {
  return invoke<{ conversations: AgentConversation[] }>("chat_conversations", {});
}

export function createAgentConversation(analysisId?: string | null): Promise<ApiResult<{ conversation: AgentConversation }>> {
  return invoke<{ conversation: AgentConversation }>("chat_conversation_create", analysisId ? { analysisId } : {});
}

export function fetchAgentConversationMessages(conversationId: string): Promise<ApiResult<{ messages: AgentChatMessage[] }>> {
  return invoke<{ messages: AgentChatMessage[] }>("chat_history", { conversationId });
}

export function sendAgentConversationMessage(input: { conversationId: string; text: string; clientMessageId: string; valueFilters?: ValueOddsFilters }): Promise<ApiResult<{ messageId: string; reply: string }>> {
  return invoke<{ messageId: string; reply: string }>("chat", input);
}

export async function sendAgentConversationMessageWithProgress(
  input: { conversationId: string; text: string; clientMessageId: string; valueFilters?: ValueOddsFilters },
  onProgress: (progress: { completed: number; total: number }) => void,
  onStatus?: (message: string) => void,
): Promise<ApiResult<{ messageId: string; reply: string }>> {
  if (!supabase || !supabaseEnv.url || !supabaseEnv.key) {
    return { ok: false, error: "not_configured", message: "Supabase não configurado" };
  }
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session?.access_token) return { ok: false, error: "missing_token" };

  const response = await fetch(`${supabaseEnv.url}/functions/v1/analysis`, {
    method: "POST",
    headers: {
      apikey: supabaseEnv.key,
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      Accept: "application/x-ndjson",
    },
    body: JSON.stringify({ action: "chat", ...input, progress: true }),
  });
  if (!response.ok || !response.body) {
    let payload: { error?: string; message?: string } = {};
    try { payload = await response.json() as typeof payload; } catch { /* resposta HTTP sem JSON */ }
    return { ok: false, error: payload.error ?? "internal_error", message: payload.message ?? `HTTP ${response.status}` };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: ApiResult<{ messageId: string; reply: string }> | null = null;
  const consumeLine = (line: string) => {
    if (!line.trim()) return;
    const event = JSON.parse(line) as {
      type?: string;
      completed?: number;
      total?: number;
      message?: string;
      error?: string;
      data?: { ok?: boolean; data?: { messageId: string; reply: string }; error?: string; message?: string };
    };
    if (event.type === "status" && event.message) onStatus?.(event.message);
    else if (event.type === "progress" && Number.isFinite(event.completed) && Number.isFinite(event.total)) {
      onProgress({ completed: event.completed!, total: event.total! });
    } else if (event.type === "result" && event.data?.ok && event.data.data) {
      result = { ok: true, data: event.data.data };
    } else if (event.type === "error") {
      result = { ok: false, error: event.error ?? "internal_error" };
    }
  };
  try {
    for (;;) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) consumeLine(line);
      if (done) break;
    }
    if (buffer.trim()) consumeLine(buffer);
  } catch (error) {
    return { ok: false, error: "internal_error", message: error instanceof Error ? error.message : "Falha ao ler o progresso da análise." };
  }
  return result ?? { ok: false, error: "internal_error", message: "A busca terminou sem retornar um resultado." };
}

export type { AnalysisListItem, Fixture };
